import {
  Accessor,
  batch,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  JSXElement,
  ParentComponent,
  Setter,
  untrack,
  useContext,
} from 'solid-js';
import { useQueryClient } from '@tanstack/solid-query';
import type { ColorPaletteType } from '@data-access/geospatial/measurements-service';
import {
  EditableStop,
  TextureType,
  EditablePalette,
} from './types/heatmap.types';
import {
  HeatmapPaletteApiService,
  type PaletteWriteVars,
} from './heatmap-palette-api-service';
import { BACKEND_DEFAULT_PALETTE } from './heatmap-palette-defaults';
import { buildProjectTenantTrn } from '@trimble/utilities';
import {
  heatmapPaletteAnalyticsProperties,
  paletteCountAfterWrite,
  type HeatmapPaletteAnalyticsPayload,
} from './heatmap-palette-analytics';

/** Whether the edit panel is adding a new palette or editing an existing one. */
export type EditMode = 'add' | 'edit';

export interface SavePaletteCallbacks {
  onSuccess?(): void;
  onError?(error: unknown): void;
}

export interface VolumeHeatmapContextValue {
  /** The full default palette, or null while loading. */
  readonly defaultPalette: Accessor<EditablePalette | null>;
  /** The colour stops for the default palette (empty while loading). */
  readonly defaultStops: Accessor<readonly EditableStop[]>;
  /** The texture type of the default palette ('gradient' | 'stepped'). */
  readonly defaultTextureType: Accessor<TextureType>;

  /** Id of the palette selected for viewing or editing, or null if none. */
  readonly selectedPaletteId: Accessor<string | null>;
  /** Set the id of the palette being viewed/edited. */
  readonly setSelectedPaletteId: Setter<string | null>;
  /** Palette resolved from the server list and {@link selectedPaletteId}. */
  readonly selectedPalette: Accessor<EditablePalette | null>;

  /** Whether the edit panel is adding a new palette or editing an existing one. */
  readonly editMode: Accessor<EditMode>;
  /** Set the current edit mode ('add' | 'edit'). */
  readonly setEditMode: Setter<EditMode>;
  /** The full list of available palettes sourced from the API service. Empty while loading. */
  readonly availablePalettes: Accessor<readonly EditablePalette[]>;
  /**
   * Whether the Color Palette API feature is enabled. When false, tiles are not
   * recoloured from a per-measurement palette and only the default palette is used.
   */
  readonly colorPaletteEnabled: Accessor<boolean>;

  /** The project id passed to the provider. */
  readonly projectId: Accessor<string>;

  /**
   * Catalogue this provider reads and writes. Volume uses CutFill; pass count
   * uses PassCount.
   */
  readonly paletteType: Accessor<ColorPaletteType>;

  /**
   * The Trimble Connect project TRN, or undefined when no region is configured.
   * Required by the palette write mutations when the Color Palette API is enabled.
   */
  readonly tenantTrn: Accessor<string | undefined>;

  /**
   * Persist changes to the currently selected palette. The selection id is only
   * reconciled to the backend result on success, so a failed write never leaves
   * a phantom (client-id) palette selected. Callbacks resolve after the backend
   * confirms the write.
   */
  savePalette(palette: EditablePalette, callbacks?: SavePaletteCallbacks): void;

  /** Select the project default palette (or the first available palette). */
  selectDefaultPalette(): void;

  /** Enter add-palette mode, preserving the current selection for cancel. */
  beginAddPalette(): void;

  /** Restore selection when leaving add mode without saving. */
  cancelPaletteEdit(): void;

  /** Clear add-mode snapshot after a successful save. */
  completePaletteEdit(): void;

  setUseDefaultPalette(useDefault: boolean): void;

  readonly lastCreatedPaletteId: Accessor<string | undefined>;

  clearLastCreatedPaletteId(): void;

  reportPaletteAnalytics(payload: HeatmapPaletteAnalyticsPayload): void;
}

const VolumeHeatmapContext = createContext<VolumeHeatmapContextValue>();

interface VolumeHeatmapProviderProps {
  /** Connect ProjectId */
  projectId: string;
  /** Feature flag to determine if all palettes from the API are available or just the default. */
  enableVolumeHeatmapColorPalette: boolean;
  /**
   * Measurement palette catalogue. Defaults to CutFill. Pass count panels and
   * tilesets pass PassCount so list and create calls use that palette type.
   */
  paletteType?: ColorPaletteType;
  /**
   * Trimble Connect region for the project. Combined with `projectId` to build
   * the tenant TRN used when reading palettes from the ColorPalettesAPI.
   */
  tcRegion?: string;
  /** Host analytics callback for palette create / edit / delete / default / change. */
  onPaletteAnalytics?(payload: HeatmapPaletteAnalyticsPayload): void;
}

export const VolumeHeatmapProvider: ParentComponent<
  VolumeHeatmapProviderProps
> = (props): JSXElement => {
  const [selectedPaletteId, setSelectedPaletteId] = createSignal<string | null>(
    null
  );

  const [useDefaultPalette, setUseDefaultPalette] = createSignal(false);

  const projectId = (): string => props.projectId;
  const paletteType = (): ColorPaletteType => props.paletteType ?? 'CutFill';

  const queryClient = useQueryClient();
  const heatmapPaletteApiService = new HeatmapPaletteApiService(
    queryClient,
    untrack(paletteType)
  );

  const tenantTrn = (): string | undefined =>
    props.tcRegion
      ? buildProjectTenantTrn(props.projectId, props.tcRegion)
      : undefined;

  const allPalettesQuery = heatmapPaletteApiService.useAllPalettesQuery(
    tenantTrn,
    () => props.enableVolumeHeatmapColorPalette
  );

  const updateMutation = heatmapPaletteApiService.updatePaletteMutation();
  const addMutation = heatmapPaletteApiService.addPaletteMutation();

  const availablePalettes = createMemo(() => {
    if (props.enableVolumeHeatmapColorPalette) {
      return allPalettesQuery.data ?? [];
    }

    // With the flag off there is no palette catalogue to edit: the heatmap uses
    // the single built-in default palette.
    return [BACKEND_DEFAULT_PALETTE];
  });

  const selectedPalette = createMemo((): EditablePalette | null => {
    const id = selectedPaletteId();
    if (id === null) {
      return null;
    }
    return availablePalettes().find((palette) => palette.id === id) ?? null;
  });

  createEffect(() => {
    const palettes = availablePalettes();
    const shouldApplyDefault = useDefaultPalette();
    if (
      shouldApplyDefault &&
      palettes.length > 0 &&
      untrack(selectedPaletteId) === null
    ) {
      const defaultPalette =
        palettes.find((palette) => palette.default) ?? palettes[0];
      setSelectedPaletteId(defaultPalette?.id ?? null);
    }
  });

  const defaultPalette = createMemo((): EditablePalette | null => {
    const palettes = availablePalettes();
    if (palettes.length === 0) return null;
    return palettes.find((p) => p.default) ?? palettes[0];
  });

  const defaultStops = createMemo(() => defaultPalette()?.stops ?? []);

  const defaultTextureType = createMemo<TextureType>(
    () => defaultPalette()?.textureType ?? 'gradient'
  );

  const [editMode, setEditMode] = createSignal<EditMode>('edit');

  const [paletteIdSnapshotBeforeAdd, setPaletteIdSnapshotBeforeAdd] =
    createSignal<string | null>(null);

  const [lastCreatedPaletteId, setLastCreatedPaletteId] = createSignal<
    string | undefined
  >();

  function clearLastCreatedPaletteId(): void {
    setLastCreatedPaletteId(undefined);
  }

  function savePalette(
    palette: EditablePalette,
    callbacks?: SavePaletteCallbacks
  ): void {
    const exists = availablePalettes().some((p) => p.id === palette.id);
    const vars: PaletteWriteVars = {
      palette,
      tenantTrn: tenantTrn(),
    };
    const mutation = exists ? updateMutation : addMutation;
    // Only reconcile the selection once the backend confirms the write. For a
    // new palette this swaps in the backend-assigned id; on failure the
    // selection is left untouched so no phantom (client-id) palette is selected
    // and the caller can keep the editor open with the draft intact.
    mutation.mutate(vars, {
      onSuccess: (saved) => {
        const palettes = availablePalettes();
        if (!exists) {
          reportPaletteAnalytics({
            action: 'created',
            ...heatmapPaletteAnalyticsProperties(
              saved,
              paletteCountAfterWrite(palettes, saved.id, 'create')
            ),
          });
        } else {
          reportPaletteAnalytics({
            action: 'edited',
            ...heatmapPaletteAnalyticsProperties(
              saved,
              paletteCountAfterWrite(palettes, saved.id, 'unchanged')
            ),
          });
        }
        // The edit panel may have been dismissed before the mutation resolved;
        // only reconcile when the write is still relevant.
        batch(() => {
          const mode = untrack(editMode);
          const currentSelectionId = untrack(selectedPaletteId);
          if (mode === 'add' || currentSelectionId === palette.id) {
            setSelectedPaletteId(saved.id);
          }
          // A freshly created palette has no measurement pointing at it yet;
          // flag it so the style panel can promote it to the active selection.
          if (!exists) {
            setLastCreatedPaletteId(saved.id);
          }
          callbacks?.onSuccess?.();
        });
      },
      onError: (error) => {
        callbacks?.onError?.(error);
      },
    });
  }

  function selectDefaultPalette(): void {
    const palettes = availablePalettes();
    if (palettes.length === 0) {
      setSelectedPaletteId(null);
      return;
    }
    const defaultSelection =
      palettes.find((palette) => palette.default) ?? palettes[0];
    setSelectedPaletteId(defaultSelection?.id ?? null);
  }

  function beginAddPalette(): void {
    setPaletteIdSnapshotBeforeAdd(untrack(selectedPaletteId));
    setSelectedPaletteId(null);
    setEditMode('add');
  }

  function cancelPaletteEdit(): void {
    if (editMode() !== 'add') {
      return;
    }
    const snapshotId = paletteIdSnapshotBeforeAdd();
    if (snapshotId) {
      setSelectedPaletteId(snapshotId);
    } else {
      selectDefaultPalette();
    }
    setPaletteIdSnapshotBeforeAdd(null);
    setEditMode('edit');
  }

  function completePaletteEdit(): void {
    setPaletteIdSnapshotBeforeAdd(null);
    setEditMode('edit');
  }

  function reportPaletteAnalytics(
    payload: HeatmapPaletteAnalyticsPayload
  ): void {
    props.onPaletteAnalytics?.(payload);
  }

  return (
    <VolumeHeatmapContext.Provider
      value={{
        projectId,
        paletteType,
        tenantTrn,
        defaultPalette,
        defaultStops,
        defaultTextureType,
        selectedPaletteId,
        setSelectedPaletteId,
        selectedPalette,
        editMode,
        setEditMode,
        availablePalettes,
        colorPaletteEnabled: () => props.enableVolumeHeatmapColorPalette,
        savePalette,
        selectDefaultPalette,
        beginAddPalette,
        cancelPaletteEdit,
        completePaletteEdit,
        setUseDefaultPalette,
        lastCreatedPaletteId,
        clearLastCreatedPaletteId,
        reportPaletteAnalytics,
      }}
    >
      {props.children}
    </VolumeHeatmapContext.Provider>
  );
};

export function useVolumeHeatmap(): VolumeHeatmapContextValue {
  const context = useContext(VolumeHeatmapContext);
  if (!context)
    throw new Error(
      'useVolumeHeatmap must be used within a VolumeHeatmapProvider'
    );
  return context;
}
